import { arrayMove } from '@dnd-kit/sortable'
import { createParser, useQueryState } from 'nuqs'
import {
  createContext,
  KeyboardEvent,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { createStaticTab, isDataGovernanceDashboardTab } from './tabs/factories'
import {
  isDatabaseMetadataTab,
  isDatabaseSchemaTab,
  isDatabaseTableTab,
  isStaticTab,
  isTurnStepTab,
  isTurnTab,
  isWorkflowTab,
  TAB_TYPE_CHAT,
  TabType,
  TabTypeStatic,
  type Tab,
} from './tabs/types'
import { parseCompactState, serializeToCompactState } from './tabs/url-serializer'

enum TabsPanelId {
  Left = 'tabs-left',
  Right = 'tabs-right',
}

type SplitViewState = {
  tabsLeft: Tab[]
  tabsRight: Tab[]
  activeTabIdLeft: string | null
  activeTabIdRight: string | null
  enableSplitView: boolean
}

type TabSearchOptions = {
  /** Filter by specific tab ID */
  tabId?: string
  /** Filter by single tab type */
  tabType?: TabType
  /** Filter by multiple tab types (OR logic within types) */
  tabTypes?: TabType[]
  /** Custom predicate function for complex filtering.
   *
   * For filtering, we want to REMOVE tabs that match the criteria
   */
  predicate?: (tab: Tab) => boolean
}

type TabPositionMetadata = {
  panelId: TabsPanelId | null
  index: number | null
  tab: Tab | null
  isActive: boolean
}

type AddTabOptions = {
  tab: Tab
  // replaceTabId?: string | null
  panelId?: TabsPanelId | null
  index?: number | null
}

type UpdateTabOptions = {
  tabId: string
  updates: Partial<Tab>
}

type GetPanelStateOptions = { panelId?: TabsPanelId | null; tabId?: string | null }

type SplitViewContextValue = SplitViewState & {
  getPanelState: (options: GetPanelStateOptions) => {
    tabs: Tab[]
    activeTabId: string | null
    panelId: TabsPanelId
  }
  addTab: (options: AddTabOptions) => void
  updateTab: (options: UpdateTabOptions) => void
  filterTabs: (options: TabSearchOptions) => void
  moveTab: (fromId: string, toId: string) => void
  tabKeyDownHandler: (e: KeyboardEvent, panelId: TabsPanelId) => void
  toggleSplitView: () => void
  panelWidth: number | null
  setPanelWidth: (width: number | null) => void
  getTabPositionMetadata: (options: TabSearchOptions) => TabPositionMetadata | null
  setActiveTabForPanel: (panelId: TabsPanelId, tabId: string | null) => void
}

const SPLIT_VIEW_QUERY_KEY = 'view'

const SplitViewProvider = ({ children }: PropsWithChildren) => {
  const [panelWidth, setPanelWidth] = useState<number | null>(null)
  const [splitViewState, setSplitViewState] = useQueryState(
    SPLIT_VIEW_QUERY_KEY,
    _splitViewParser.withDefault(createDefaultSplitViewState()).withOptions({
      clearOnDefault: true,
      history: 'push',
    }),
  )

  const { tabsLeft, tabsRight, activeTabIdLeft, activeTabIdRight, enableSplitView } = splitViewState

  const addTab: SplitViewContextValue['addTab'] = useCallback(
    (options) => setSplitViewState((prev) => _addTab(prev, options)),
    [setSplitViewState],
  )

  const updateTab: SplitViewContextValue['updateTab'] = useCallback(
    (options) => setSplitViewState((prev) => _updateTab(prev, options)),
    [setSplitViewState],
  )

  const getTabPositionMetadata = useCallback(
    (options: TabSearchOptions) => _getTabPositionMetadata(options, splitViewState),
    [splitViewState],
  )

  const toggleSplitView: SplitViewContextValue['toggleSplitView'] = useCallback(
    () => setSplitViewState(_toggleSplitView),
    [setSplitViewState],
  )

  const filterTabs: SplitViewContextValue['filterTabs'] = useCallback(
    (options) => setSplitViewState((prev) => _filterSplitViewTabs(prev, options)),
    [setSplitViewState],
  )

  const moveTab: SplitViewContextValue['moveTab'] = useCallback(
    (fromId, toId) => {
      setSplitViewState((prev) => _moveTab(prev, fromId, toId))
    },
    [setSplitViewState],
  )

  const tabKeyDownHandler: SplitViewContextValue['tabKeyDownHandler'] = useCallback(
    (e, panelId) => {
      setSplitViewState((prev) => {
        const keyboardUpdate = _handleTabKeyDown(prev, panelId, e.key)

        if (keyboardUpdate) {
          return { ...prev, ...keyboardUpdate }
        }

        if (e.key === ' ') {
          e.preventDefault()
        }

        return prev
      })
    },
    [setSplitViewState],
  )

  const getPanelState: SplitViewContextValue['getPanelState'] = useCallback(
    (options) => _panelUtils.getPanelState(options, splitViewState),
    [splitViewState],
  )

  const setActiveTabForPanel: SplitViewContextValue['setActiveTabForPanel'] = useCallback(
    (panelId, tabId) => {
      setSplitViewState((prev) => {
        const currentActiveTabId = _panelUtils.getActiveTabIdForPanel(panelId, prev)
        if (currentActiveTabId === tabId) return prev

        const newState = _panelUtils.setActiveTabIdForPanel(panelId, tabId)
        return _autoManageSplitView({ ...prev, ...newState })
      })
    },
    [setSplitViewState],
  )

  const value = useMemo(
    (): SplitViewContextValue => ({
      getTabPositionMetadata,
      tabsLeft,
      tabsRight,
      activeTabIdLeft,
      activeTabIdRight,
      getPanelState,
      addTab,
      updateTab,
      filterTabs,
      moveTab,
      tabKeyDownHandler,
      enableSplitView,
      toggleSplitView,
      panelWidth,
      setPanelWidth,
      setActiveTabForPanel,
    }),
    [
      getTabPositionMetadata,
      tabsLeft,
      tabsRight,
      activeTabIdLeft,
      activeTabIdRight,
      getPanelState,
      addTab,
      updateTab,
      filterTabs,
      moveTab,
      tabKeyDownHandler,
      enableSplitView,
      toggleSplitView,
      panelWidth,
      setPanelWidth,
      setActiveTabForPanel,
    ],
  )

  return <SplitViewContext.Provider value={value}>{children}</SplitViewContext.Provider>
}

const SplitViewContext = createContext<SplitViewContextValue | null>(null)

// Returns the active tab id based on strict priority: targetTabId > lastActiveTabId > first tab
const _getActiveTabId = ({
  tabs,
  targetTabId,
  lastActiveTabId,
}: {
  tabs: Tab[]
  targetTabId?: string | null
  lastActiveTabId?: string | null
}): string | null => {
  if (tabs.length === 0) return null

  // 1. Prioritize targetTabId if present in tabs
  if (targetTabId && tabs.some((tab) => tab.id === targetTabId)) {
    return targetTabId
  }

  // 2. Then try lastActiveTabId if present in tabs
  if (lastActiveTabId && tabs.some((tab) => tab.id === lastActiveTabId)) {
    return lastActiveTabId
  }

  // 3. Otherwise, fall back to the first tab (guaranteed to exist)
  return tabs[0].id
}

// Panel utility functions
const _panelUtils = {
  getPanelIdByTabId: (tabId: string, state: SplitViewState): TabsPanelId | null => {
    if (state.tabsLeft.some((t: Tab) => t.id === tabId)) return TabsPanelId.Left
    if (state.tabsRight.some((t: Tab) => t.id === tabId)) return TabsPanelId.Right
    return null
  },

  getPanelIdByDropId: (dropId: string, state: SplitViewState): TabsPanelId | null => {
    if (dropId === TabsPanelId.Left || dropId === TabsPanelId.Right) return dropId
    return _panelUtils.getPanelIdByTabId(dropId, state)
  },

  getPanelState: (
    options: GetPanelStateOptions,
    state: SplitViewState,
  ): { tabs: Tab[]; activeTabId: string | null; panelId: TabsPanelId } => {
    const panelId =
      options.panelId ??
      (options.tabId ? _panelUtils.getPanelIdByTabId(options.tabId, state) : TabsPanelId.Left) ??
      TabsPanelId.Left
    const tabs = panelId === TabsPanelId.Left ? state.tabsLeft : state.tabsRight
    const activeTabId =
      panelId === TabsPanelId.Left ? state.activeTabIdLeft : state.activeTabIdRight

    return { tabs, activeTabId, panelId }
  },

  getActiveTabIdForPanel: (panelId: TabsPanelId, state: SplitViewState): string | null => {
    return panelId === TabsPanelId.Left ? state.activeTabIdLeft : state.activeTabIdRight
  },

  getTabIndex: (tabId: string, state: SplitViewState): number | null => {
    return (
      state.tabsLeft.findIndex((t) => t.id === tabId) ??
      state.tabsRight.findIndex((t) => t.id === tabId) ??
      null
    )
  },

  getTabsForPanel: (panelId: TabsPanelId, state: SplitViewState): Tab[] => {
    return panelId === TabsPanelId.Left ? state.tabsLeft : state.tabsRight
  },

  setTabsForPanel: (panelId: TabsPanelId, tabs: Tab[]): Partial<SplitViewState> => {
    return panelId === TabsPanelId.Left ? { tabsLeft: tabs } : { tabsRight: tabs }
  },

  setActiveTabIdForPanel: (panelId: TabsPanelId, tabId: string | null): Partial<SplitViewState> => {
    return panelId === TabsPanelId.Left ? { activeTabIdLeft: tabId } : { activeTabIdRight: tabId }
  },
}

/**
 * Utility function to validate search options
 */
const _validateSearchOptions = (options: TabSearchOptions): boolean => {
  if (!options || typeof options !== 'object') {
    return false
  }

  const hasValidCriteria = Boolean(
    options.tabId || options.tabType || options.tabTypes || options.predicate,
  )

  if (!hasValidCriteria) {
    return false
  }

  // Validate tabTypes array if provided
  if (options.tabTypes && (!Array.isArray(options.tabTypes) || options.tabTypes.length === 0)) {
    return false
  }

  // Validate predicate function if provided
  if (options.predicate && typeof options.predicate !== 'function') {
    return false
  }

  return true
}

/**
 * Utility function to create a debug-friendly description of search options
 */
const _describeSearchOptions = (options: TabSearchOptions): string => {
  const parts: string[] = []

  if (options.tabId) parts.push(`tabId: ${options.tabId}`)
  if (options.tabType) parts.push(`tabType: ${options.tabType}`)
  if (options.tabTypes) parts.push(`tabTypes: [${options.tabTypes.join(', ')}]`)
  if (options.predicate) parts.push('predicate: function')

  return parts.length > 0 ? parts.join(', ') : 'no criteria'
}

const _getTabPositionMetadata = (
  options: TabSearchOptions,
  state: SplitViewState,
): TabPositionMetadata | null => {
  // Validate input
  if (!options || !state) {
    console.warn('Invalid arguments passed to _getTabPositionMetadata')
    return null
  }

  if (!_validateSearchOptions(options)) {
    console.warn(`Invalid search options: ${_describeSearchOptions(options)}`)
    return null
  }

  // Build search function
  const searchFn = _buildSearchFn(options)
  if (!searchFn) {
    console.warn('No valid search criteria provided to _getTabPositionMetadata')
    return null
  }

  // Search in both panels
  const leftIndex = state.tabsLeft.findIndex(searchFn)
  if (leftIndex !== -1) {
    const tab = state.tabsLeft[leftIndex]
    return {
      panelId: TabsPanelId.Left,
      index: leftIndex,
      tab,
      isActive: state.activeTabIdLeft === tab.id,
    }
  }

  const rightIndex = state.tabsRight.findIndex(searchFn)
  if (rightIndex !== -1) {
    const tab = state.tabsRight[rightIndex]
    return {
      panelId: TabsPanelId.Right,
      index: rightIndex,
      tab,
      isActive: state.activeTabIdRight === tab.id,
    }
  }

  return null
}

const _insertTab = ({
  state,
  tab,
  panelId,
  index,
}: {
  state: SplitViewState
  tab: Tab
  panelId?: TabsPanelId | null
  index?: number | null
}): SplitViewState => {
  const insert = (tabs: Tab[]) => {
    const result =
      typeof index === 'number'
        ? [...tabs.slice(0, index), tab, ...tabs.slice(index)]
        : [...tabs, tab]

    return result
  }

  if (!panelId || panelId === TabsPanelId.Left) {
    const result = {
      ...state,
      tabsLeft: insert(state.tabsLeft),
      activeTabIdLeft: tab.id,
    }

    return result
  }

  const result = {
    ...state,
    tabsRight: insert(state.tabsRight),
    activeTabIdRight: tab.id,
    enableSplitView: true,
  }

  return result
}

/**
 * Helper function to update an existing tab in the split view
 */
const _updateTab = (
  state: SplitViewState,
  { tabId, updates }: UpdateTabOptions,
): SplitViewState => {
  const updateTabInArray = (tabs: Tab[]): Tab[] => {
    return tabs.map((tab) => {
      if (tab.id === tabId) {
        return { ...tab, ...updates } as Tab
      }
      return tab
    })
  }

  return {
    ...state,
    tabsLeft: updateTabInArray(state.tabsLeft),
    tabsRight: updateTabInArray(state.tabsRight),
  }
}

/**
 * Helper function to add a new tab to the split view
 */
const _addTab = (state: SplitViewState, { tab, panelId, index }: AddTabOptions): SplitViewState => {
  if (isWorkflowTab(tab)) {
    return _insertTab({
      tab,
      state: _filterSplitViewTabs(
        state,
        {
          predicate: (t) => t.type === TAB_TYPE_CHAT && t.workflowId === tab.workflowId,
        },
        true,
      ),
      panelId,
      index: index ?? 0,
    })
  }

  if (isStaticTab(tab)) {
    return _insertTab({
      state: _filterSplitViewTabs(state, { tabType: tab.type }, true),
      tab,
      panelId,
      index,
    })
  }

  if (isTurnTab(tab)) {
    return _insertTab({
      state: _filterSplitViewTabs(
        state,
        {
          predicate: (t) =>
            t.type === tab.type && t.turnId === tab.turnId && t.workflowId === tab.workflowId,
        },
        true,
      ),
      tab,
      panelId,
      index,
    })
  }

  if (isTurnStepTab(tab)) {
    return _insertTab({
      state: _filterSplitViewTabs(
        state,
        {
          predicate: (t) =>
            t.type === tab.type &&
            t.turnId === tab.turnId &&
            t.stepId === tab.stepId &&
            t.workflowId === tab.workflowId,
        },
        true,
      ),
      tab,
      panelId,
      index,
    })
  }

  if (isDatabaseSchemaTab(tab)) {
    return _insertTab({
      state: _filterSplitViewTabs(
        state,
        {
          predicate: (t) =>
            t.type === tab.type && isDatabaseSchemaTab(t) && t.databaseName === tab.databaseName,
        },
        true,
      ),
      tab,
      panelId,
      index,
    })
  }

  if (isDatabaseTableTab(tab)) {
    return _insertTab({
      state: _filterSplitViewTabs(
        state,
        {
          predicate: (t) =>
            t.type === tab.type &&
            isDatabaseTableTab(t) &&
            t.databaseName === tab.databaseName &&
            t.schemaName === tab.schemaName,
        },
        true,
      ),
      tab,
      panelId,
      index,
    })
  }

  if (isDatabaseMetadataTab(tab)) {
    return _insertTab({
      state: _filterSplitViewTabs(
        state,
        {
          predicate: (t) =>
            t.type === tab.type &&
            isDatabaseMetadataTab(t) &&
            t.databaseName === tab.databaseName &&
            t.schemaName === tab.schemaName &&
            t.tableName === tab.tableName,
        },
        true,
      ),
      tab,
      panelId,
      index,
    })
  }

  if (isDataGovernanceDashboardTab(tab)) {
    return _insertTab({
      state: _filterSplitViewTabs(
        state,
        {
          predicate: (t) =>
            t.type === tab.type &&
            isDataGovernanceDashboardTab(t) &&
            t.tabView === tab.tabView &&
            (tab.databaseName === 'all' || t.databaseName === tab.databaseName) &&
            t.schemaName === tab.schemaName &&
            t.tableName === tab.tableName &&
            t.sensitivity === tab.sensitivity,
        },
        true,
      ),
      tab,
      panelId,
      index,
    })
  }

  return _insertTab({
    state,
    tab,
    panelId,
    index,
  })
}

/**
 * Builds a filter function for removing tabs based on search options.
 * This function returns true for tabs that should be KEPT (not removed).
 */
const _buildFilterFn = ({
  tabId,
  tabType,
  tabTypes,
  predicate,
}: TabSearchOptions): ((tab: Tab) => boolean) | null => {
  const filters: ((tab: Tab) => boolean)[] = []

  // For filtering, we want to KEEP tabs that don't match the criteria
  if (tabId && typeof tabId === 'string') {
    filters.push((t) => t.id !== tabId)
  }

  if (tabType) {
    filters.push((t) => t.type !== tabType)
  }

  if (tabTypes && Array.isArray(tabTypes) && tabTypes.length > 0) {
    filters.push((t) => !tabTypes.includes(t.type))
  }

  if (predicate && typeof predicate === 'function') {
    // For predicate, we want to KEEP tabs that don't match the predicate
    filters.push((t) => !predicate(t))
  }

  // If no filters specified, return null (no filtering)
  if (filters.length === 0) {
    return null
  }

  // Combine all filters with AND logic - tab must pass ALL filters to be kept
  return (tab) => {
    try {
      return filters.every((filter) => filter(tab))
    } catch (error) {
      console.error('❌ Error in filter function:', error)
      return true // Keep tab if filter fails
    }
  }
}

/**
 * Builds a search function for finding tabs based on search options.
 * This function returns true for tabs that match the search criteria.
 */
const _buildSearchFn = ({
  tabId,
  tabType,
  tabTypes,
  predicate,
}: TabSearchOptions): ((tab: Tab) => boolean) | null => {
  const searchFns: ((tab: Tab) => boolean)[] = []

  if (tabId && typeof tabId === 'string') {
    searchFns.push((t) => t.id === tabId)
  }

  if (tabType) {
    searchFns.push((t) => t.type === tabType)
  }

  if (tabTypes && Array.isArray(tabTypes) && tabTypes.length > 0) {
    searchFns.push((t) => tabTypes.includes(t.type))
  }

  if (predicate && typeof predicate === 'function') {
    searchFns.push(predicate)
  }

  // If no search criteria specified, return null
  if (searchFns.length === 0) {
    return null
  }

  // For search, we use OR logic within the same category, but AND logic across different categories
  // This means: (tabId OR tabType OR tabTypes OR predicate) - but if multiple are specified, all must match
  if (searchFns.length === 1) {
    return searchFns[0]
  }

  // Multiple criteria: all must match (AND logic)
  return (tab) => {
    try {
      return searchFns.every((searchFn) => searchFn(tab))
    } catch (error) {
      console.error('Error in search function:', error)
      return false // Don't match if search fails
    }
  }
}

/**
 * Filters tabs based on search options and returns a new state with filtered tabs.
 * This function removes tabs that match the filter criteria.
 */
const _filterSplitViewTabs = (
  state: SplitViewState,
  searchOptions: TabSearchOptions,
  allowEmpty: boolean = false,
): SplitViewState => {
  // Validate input
  if (!state || !searchOptions) {
    console.warn('❌ Invalid arguments passed to _filterSplitViewTabs')
    return state
  }

  if (!_validateSearchOptions(searchOptions)) {
    console.warn(`❌ Invalid filter options: ${_describeSearchOptions(searchOptions)}`)
    return state
  }

  const filterFn = _buildFilterFn(searchOptions)
  const returnFn = allowEmpty
    ? (state: SplitViewState) => ({ ...state, ..._normalizePanelState(state) })
    : _autoManageSplitView

  // If no filter function, return original state unchanged
  if (!filterFn) {
    return returnFn(state)
  }

  // Apply filter to both panels
  const newLeft = state.tabsLeft.filter(filterFn)
  const newRight = state.tabsRight.filter(filterFn)

  return returnFn({
    ...state,
    tabsLeft: newLeft,
    tabsRight: newRight,
    activeTabIdLeft: _getActiveTabId({ tabs: newLeft, lastActiveTabId: state.activeTabIdLeft }),
    activeTabIdRight: _getActiveTabId({
      tabs: newRight,
      lastActiveTabId: state.activeTabIdRight,
    }),
  })
}

const _autoManageSplitView = (state: SplitViewState): SplitViewState => {
  if (state.tabsLeft.length === 0 && state.tabsRight.length === 0) {
    return createDefaultSplitViewState()
  }

  if (state.tabsLeft.length === 0) {
    // Move all tabs from right to left panel
    const newLeft = [...state.tabsRight]
    return {
      ...state,
      tabsLeft: newLeft,
      tabsRight: [],
      activeTabIdLeft: _getActiveTabId({ tabs: newLeft, lastActiveTabId: state.activeTabIdRight }),
      activeTabIdRight: null,
      enableSplitView: false,
    }
  }

  if (state.tabsRight.length === 0) {
    // Ensure left panel has active tab
    return {
      ...state,
      activeTabIdLeft: _getActiveTabId({
        tabs: state.tabsLeft,
        lastActiveTabId: state.activeTabIdLeft,
      }),
      activeTabIdRight: null,
      enableSplitView: false,
    }
  }

  // Both panels have tabs - enable split view
  return {
    ...state,
    enableSplitView: true,
    activeTabIdLeft: _getActiveTabId({
      tabs: state.tabsLeft,
      lastActiveTabId: state.activeTabIdLeft,
    }),
    activeTabIdRight: _getActiveTabId({
      tabs: state.tabsRight,
      lastActiveTabId: state.activeTabIdRight,
    }),
    // Note: We don't move tabs here - they stay in their current panels
    // Split view is enabled when there are tabs in both panels
  }
}

/**
 * Helper function to toggle split view state
 */
const _toggleSplitView = (state: SplitViewState): SplitViewState => {
  if (state.enableSplitView) {
    // Disable split view - move all tabs to left panel
    const allTabs = [...state.tabsLeft, ...state.tabsRight]
    const activeTabId = state.activeTabIdLeft || state.activeTabIdRight

    return {
      ...state,
      tabsLeft: allTabs,
      tabsRight: [],
      activeTabIdLeft: activeTabId,
      activeTabIdRight: null,
      enableSplitView: false,
    }
  } else {
    // Enable split view - keep all tabs in left panel and create a blank tab in right panel

    const blankTab = createStaticTab(TabTypeStatic.Blank)
    const filtered = _filterSplitViewTabs(state, { tabType: TabTypeStatic.Blank }, true)

    return {
      ...filtered,
      tabsRight: [blankTab], // Create blank tab in right panel
      activeTabIdRight: blankTab.id, // Set blank tab as active in right panel
      enableSplitView: true,
    }
  }
}

const _handleTabMoveWithinPanel = (
  state: SplitViewState,
  panelId: TabsPanelId,
  fromIndex: number,
  toIndex: number,
): Partial<SplitViewState> => {
  const panelTabs = _panelUtils.getTabsForPanel(panelId, state)
  const updatedTabs = arrayMove(panelTabs, fromIndex, toIndex)
  return _panelUtils.setTabsForPanel(panelId, updatedTabs)
}

const _handleTabMoveBetweenPanels = (
  state: SplitViewState,
  fromPanel: TabsPanelId,
  toPanel: TabsPanelId,
  tabToMove: Tab,
  toIndex: number,
): Partial<SplitViewState> => {
  const fromTabs = _panelUtils.getTabsForPanel(fromPanel, state)
  const toTabs = _panelUtils.getTabsForPanel(toPanel, state)

  const updatedFromTabs = fromTabs.filter((tab: Tab) => tab.id !== tabToMove.id)
  const updatedToTabs = toTabs.filter((tab: Tab) => tab.id !== tabToMove.id)
  const insertIndex = toIndex === -1 ? updatedToTabs.length : toIndex

  updatedToTabs.splice(insertIndex, 0, tabToMove)

  return {
    ..._panelUtils.setTabsForPanel(fromPanel, updatedFromTabs),
    ..._panelUtils.setTabsForPanel(toPanel, updatedToTabs),
  }
}

const _updateActiveTabIdsAfterMove = (
  state: SplitViewState,
  fromPanel: TabsPanelId,
  toPanel: TabsPanelId,
  tabToMove: Tab,
  newLeft: Tab[],
  newRight: Tab[],
): Partial<SplitViewState> => {
  let newActiveLeft = state.activeTabIdLeft
  let newActiveRight = state.activeTabIdRight

  if (fromPanel === toPanel) {
    // Moving within same panel - set the moved tab as active
    if (fromPanel === TabsPanelId.Left) {
      newActiveLeft = tabToMove.id
    } else {
      newActiveRight = tabToMove.id
    }
  } else {
    // Moving between panels - set the target panel's active tab to the moved tab
    if (toPanel === TabsPanelId.Left) {
      newActiveLeft = tabToMove.id
      // Update source panel's active tab if the moved tab was active
      if (state.activeTabIdLeft === tabToMove.id) {
        const targetLeft = _getActiveTabId({ tabs: newLeft, targetTabId: null })
        if (targetLeft) newActiveLeft = targetLeft
      }
      if (state.activeTabIdRight === tabToMove.id) {
        newActiveRight = _getActiveTabId({ tabs: newRight, targetTabId: null })
      }
    } else {
      newActiveRight = tabToMove.id
      // Update source panel's active tab if the moved tab was active
      if (state.activeTabIdLeft === tabToMove.id) {
        const targetLeft = _getActiveTabId({ tabs: newLeft, targetTabId: null })
        if (targetLeft) newActiveLeft = targetLeft
      }
      if (state.activeTabIdRight === tabToMove.id) {
        newActiveRight = _getActiveTabId({ tabs: newRight, targetTabId: null })
      }
    }
  }

  return { activeTabIdLeft: newActiveLeft, activeTabIdRight: newActiveRight }
}

const _normalizePanelState = (state: SplitViewState): Partial<SplitViewState> => {
  let {
    tabsLeft: newLeft,
    tabsRight: newRight,
    activeTabIdLeft: newActiveLeft,
    activeTabIdRight: newActiveRight,
  } = state
  let newEnableSplitView = state.enableSplitView

  // Handle empty right panel
  if (newRight.length === 0) {
    newActiveRight = null
    newEnableSplitView = false
  }

  // Handle empty left panel - move all tabs from right to left
  if (newLeft.length === 0) {
    newLeft = [...newRight]
    newRight = []
    newActiveLeft = _getActiveTabId({ tabs: newLeft, targetTabId: newActiveLeft })
    newActiveRight = null
    newEnableSplitView = false
  }

  // Ensure active tab IDs are properly set for both panels
  if (newLeft.length > 0 && !newActiveLeft) {
    newActiveLeft = _getActiveTabId({ tabs: newLeft, targetTabId: newActiveLeft })
  }
  if (newRight.length > 0 && !newActiveRight) {
    newActiveRight = _getActiveTabId({ tabs: newRight, targetTabId: newActiveRight })
  }

  return {
    tabsLeft: newLeft,
    tabsRight: newRight,
    activeTabIdLeft: newActiveLeft,
    activeTabIdRight: newActiveRight,
    enableSplitView: newEnableSplitView,
  }
}

/**
 * Helper function to handle keyboard navigation for tabs
 */
const _handleTabKeyDown = (
  state: SplitViewState,
  panelId: TabsPanelId,
  key: string,
): Partial<SplitViewState> | null => {
  const activeTabId = _panelUtils.getActiveTabIdForPanel(panelId, state)

  if (!activeTabId) return null

  // Handle navigation keys
  if (key === 'ArrowLeft' || key === 'ArrowRight') {
    const keyboardUpdate = _handleKeyboardNavigation(state, panelId, key, activeTabId)
    if (keyboardUpdate) {
      return keyboardUpdate
    }
  }

  // Handle delete/escape/backspace keys
  if (key === 'Delete' || key === 'Escape' || key === 'Backspace') {
    if (state.tabsLeft.length + state.tabsRight.length <= 1 || !activeTabId) {
      console.warn('No tabs to delete')
      return null
    }

    return _filterSplitViewTabs(state, { tabId: activeTabId })
  }

  return null
}

const _handleKeyboardNavigation = (
  state: SplitViewState,
  panelId: TabsPanelId,
  key: string,
  activeTabId: string | null,
): Partial<SplitViewState> | null => {
  const tabs = _panelUtils.getTabsForPanel(panelId, state)
  const idx = tabs.findIndex((t: Tab) => t.id === activeTabId)

  if (tabs.length === 0 || idx === -1) return null

  const nextIndex = (i: number) => (i + 1) % tabs.length
  const prevIndex = (i: number) => (i - 1 + tabs.length) % tabs.length

  if (key === 'ArrowRight') {
    const newIdx = nextIndex(idx)
    const newActiveTabId = _getActiveTabId({ tabs, targetTabId: tabs[newIdx].id })
    return _panelUtils.setActiveTabIdForPanel(panelId, newActiveTabId)
  }

  if (key === 'ArrowLeft') {
    const newIdx = prevIndex(idx)
    const newActiveTabId = _getActiveTabId({ tabs, targetTabId: tabs[newIdx].id })
    return _panelUtils.setActiveTabIdForPanel(panelId, newActiveTabId)
  }

  return null
}

/**
 * Helper function to move a tab within or between panels
 */
const _moveTab = (state: SplitViewState, fromId: string, toId: string): SplitViewState => {
  const fromPanel = _panelUtils.getPanelIdByDropId(fromId, state)
  const toPanel = _panelUtils.getPanelIdByDropId(toId, state)

  if (!fromPanel || !toPanel) return state

  const fromState = _panelUtils.getPanelState({ panelId: fromPanel }, state)
  const toState = _panelUtils.getPanelState({ panelId: toPanel }, state)

  const fromIndex = fromState.tabs.findIndex((t) => t.id === fromId)
  const toIndex = toState.tabs.findIndex((t) => t.id === toId)

  if (fromIndex === -1) return state

  const tabToMove = fromState.tabs[fromIndex]
  let newLeft = [...state.tabsLeft]
  let newRight = [...state.tabsRight]

  if (fromPanel === toPanel) {
    // Moving within same panel
    const panelUpdate = _handleTabMoveWithinPanel(state, fromPanel, fromIndex, toIndex)
    if (fromPanel === TabsPanelId.Left) {
      newLeft = panelUpdate.tabsLeft || newLeft
    } else {
      newRight = panelUpdate.tabsRight || newRight
    }
  } else {
    // Moving between panels
    const panelUpdate = _handleTabMoveBetweenPanels(state, fromPanel, toPanel, tabToMove, toIndex)
    newLeft = panelUpdate.tabsLeft || newLeft
    newRight = panelUpdate.tabsRight || newRight
  }

  // Update active tab IDs after move
  const activeTabUpdate = _updateActiveTabIdsAfterMove(
    state,
    fromPanel,
    toPanel,
    tabToMove,
    newLeft,
    newRight,
  )

  // Normalize panel state
  const normalizedState = _normalizePanelState({
    ...state,
    tabsLeft: newLeft,
    tabsRight: newRight,
    ...activeTabUpdate,
  })

  return {
    ...state,
    ...normalizedState,
  }
}

// URL state management utilities

const _splitViewParser = createParser<SplitViewState>({
  parse: parseCompactState,
  serialize: serializeToCompactState,
})

const createDefaultSplitViewState = (): SplitViewState => {
  const defaultTab = createStaticTab(TabTypeStatic.NewChat)
  return {
    tabsLeft: [defaultTab],
    tabsRight: [],
    activeTabIdLeft: defaultTab.id,
    activeTabIdRight: null,
    enableSplitView: false,
  }
}

const useSplitView = () => {
  const ctx = useContext(SplitViewContext)
  if (!ctx) throw new Error('useSplitView must be used within a provider')
  return ctx
}

const useSplitViewQueryState = () => {
  const { enableSplitView, tabsLeft, tabsRight, activeTabIdLeft, activeTabIdRight } = useSplitView()
  return useMemo(
    () =>
      _splitViewParser.serialize({
        enableSplitView,
        tabsLeft,
        tabsRight,
        activeTabIdLeft,
        activeTabIdRight,
      }),
    [enableSplitView, tabsLeft, tabsRight, activeTabIdLeft, activeTabIdRight],
  )
}

export {
  SPLIT_VIEW_QUERY_KEY,
  SplitViewProvider,
  TabsPanelId,
  useSplitView,
  useSplitViewQueryState,
}
